/**
 * pp-customer-order — handlers para los botones del detalle de orden.
 *
 * Responsabilidades:
 *   - VOLVER A COMPRAR: lee data-pp-repurchase-items en el DOM (JSON
 *     con [{variant_id, quantity}, ...]) y los agrega al carrito vía
 *     /cart/add.js, luego abre el cart drawer.
 *   - DESCARGAR FACTURA: si la orden está facturada, resuelve el factura_id
 *     de esta orden vía /apps/crm/customer-fiscal (match por número) y pinta
 *     botones inline "Descargar PDF/XML" que bajan el blob por factura_id
 *     (signed URL fresca del edge). Fallback si no resuelve: el botón "Ver
 *     Factura" abre el modal, que ofrece las descargas igual.
 *   - CONTACTO EJECUTIVO: el mismo GET fiscal apunta WhatsApp/llamada al
 *     ejecutivo de cuenta si el cliente tiene uno.
 *   - Toast inline para feedback (success / error).
 */

(function () {
  'use strict';

  // Descarga de facturas por factura_id (edge facturacion-publica/descargar).
  // La publishable key es pública por diseño (la defensa real es RLS); ya vive
  // hardcodeada en pp-mis-facturas.js y pp-factura-modal.js.
  var FACT_BASE = 'https://dhygbnysealeblqorjts.supabase.co/functions/v1/facturacion-publica';
  var FACT_APIKEY = 'sb_publishable_48y6S7h2eQ-9uTF7pCNJNQ_Q0mz8VVX';

  function showToast(message, kind) {
    var toast = document.createElement('div');
    toast.className = 'pp-order__toast pp-order__toast--' + (kind || 'ok');
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });
    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 300);
    }, 2400);
  }

  function bindRepurchase(btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (btn.disabled) return;

      var raw = btn.getAttribute('data-pp-repurchase-items');
      if (!raw) {
        showToast('No hay artículos para reordenar', 'err');
        return;
      }

      var items;
      try {
        items = JSON.parse(raw);
      } catch (err) {
        console.warn('[pp-customer-order] invalid repurchase items JSON', err);
        showToast('Error al leer la orden', 'err');
        return;
      }

      if (!Array.isArray(items) || items.length === 0) {
        showToast('No hay artículos para reordenar', 'err');
        return;
      }

      var originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Agregando…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function () {
          showToast('Productos agregados al carrito', 'ok');
          // Notificar al cart drawer para que se abra (mismo evento que
          // dispara pp-main-product al hacer add-to-cart).
          document.dispatchEvent(new CustomEvent('pp:cart-drawer:open'));
          document.dispatchEvent(new CustomEvent('pp:cart-updated'));
        })
        .catch(function (err) {
          console.warn('[pp-customer-order] repurchase failed', err);
          showToast('Algunos artículos no están disponibles', 'err');
        })
        .finally(function () {
          btn.disabled = false;
          btn.textContent = originalText;
        });
    });
  }

  // Baja una factura (PDF o XML) por su factura_id: el edge da una signed URL
  // fresca (5 min) → fetch del blob → download programático. Copiado de
  // pp-mis-facturas.js (deuda conocida: extraer a pp-fact-core.js).
  function callFact(path) {
    return fetch(FACT_BASE + '/' + path, {
      method: 'GET',
      headers: { 'Authorization': 'Bearer ' + FACT_APIKEY, 'apikey': FACT_APIKEY }
    })
      .then(function (res) { return res.json().catch(function () { return null; }); })
      .then(function (json) { return json || { ok: false }; })
      .catch(function (err) { console.error('[pp-customer-order] fact red:', err); return { ok: false }; });
  }

  function downloadInvoiceById(facturaId, tipo, btn) {
    var original = Array.prototype.slice.call(btn.childNodes);
    btn.disabled = true;
    while (btn.firstChild) btn.removeChild(btn.firstChild);
    btn.textContent = '…';
    var restore = function () {
      while (btn.firstChild) btn.removeChild(btn.firstChild);
      original.forEach(function (n) { btn.appendChild(n); });
      btn.disabled = false;
    };
    callFact('descargar/' + facturaId + '/' + tipo)
      .then(function (result) {
        if (!result.ok || !result.data || !result.data.url) throw new Error('no_url');
        var folio = result.data.folio || facturaId;
        return fetch(result.data.url).then(function (res) {
          if (!res.ok) throw new Error('http_' + res.status);
          return res.blob();
        }).then(function (blob) {
          var url = URL.createObjectURL(blob);
          var a = document.createElement('a');
          a.href = url; a.download = 'factura-' + folio + '.' + tipo; a.style.display = 'none';
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
          setTimeout(function () { URL.revokeObjectURL(url); }, 100);
        });
      })
      .catch(function (err) {
        console.warn('[pp-customer-order] invoice download failed', err);
        showToast('No pudimos descargar la factura', 'err');
      })
      .finally(restore);
  }

  // Icono flecha-abajo construido con DOM (sin innerHTML → sin riesgo XSS).
  function svgEl(tag, attrs) {
    var el = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (var k in attrs) { if (attrs.hasOwnProperty(k)) el.setAttribute(k, attrs[k]); }
    return el;
  }
  function downloadIcon() {
    var svg = svgEl('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true' });
    svg.appendChild(svgEl('path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }));
    svg.appendChild(svgEl('polyline', { points: '7 10 12 15 17 10' }));
    svg.appendChild(svgEl('line', { x1: '12', y1: '15', x2: '12', y2: '3' }));
    return svg;
  }

  // Botón de descarga (icono + label localizado).
  function makeDlButton(facturaId, tipo, label) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'pp-orders__action pp-orders__action--download';
    b.appendChild(downloadIcon());
    b.appendChild(document.createTextNode(label));
    b.setAttribute('aria-label', label);
    b.addEventListener('click', function () { downloadInvoiceById(facturaId, tipo, b); });
    return b;
  }

  // Muestra el fallback "Ver Factura" de un slot (quita el spinner de carga y
  // revela el botón que abre el modal). Se usa cuando no hay match o falla el GET.
  function revealFallback(box) {
    var loading = box.querySelector('[data-pp-invoice-loading]');
    if (loading && loading.parentNode) loading.parentNode.removeChild(loading);
    var fb = box.querySelector('[data-pp-invoice-fallback]');
    if (fb) fb.hidden = false;
  }
  function revealAllFallbacks() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pp-invoice-dl]'), revealFallback);
  }

  // Para CADA orden facturada en la página (1 en el detalle, N en la lista),
  // sustituye el spinner de carga por descargas inline PDF/XML, matcheando por
  // número contra las facturas del CRM. Una sola respuesta `d` resuelve toda la
  // página. Sin match (o sin archivos) revela el fallback "Ver Factura" (el modal
  // ofrece las descargas igual). Así el cliente NUNCA ve un botón "equivocado"
  // que luego cambia: ve un spinner que resuelve al estado correcto.
  function resolveInvoiceDownloads(d) {
    var boxes = document.querySelectorAll('[data-pp-invoice-dl]');
    if (!boxes.length) return;
    // Índice número-de-orden → factura (normalizado a dígitos).
    var byNum = {};
    if (d && Array.isArray(d.facturas)) {
      for (var i = 0; i < d.facturas.length; i++) {
        var f = d.facturas[i];
        var key = String(f.order_number || '').replace(/\D/g, '');
        if (key && f.id && !byNum[key]) byNum[key] = f;
      }
    }
    Array.prototype.forEach.call(boxes, function (box) {
      var want = String(box.getAttribute('data-order-number') || '').replace(/\D/g, '');
      var match = want && byNum[want];
      var frag = document.createDocumentFragment();
      if (match) {
        if (match.has_pdf) frag.appendChild(makeDlButton(match.id, 'pdf', box.getAttribute('data-t-pdf') || 'Descargar PDF'));
        if (match.has_xml) frag.appendChild(makeDlButton(match.id, 'xml', box.getAttribute('data-t-xml') || 'Descargar XML'));
      }
      if (frag.childNodes.length) {
        while (box.firstChild) box.removeChild(box.firstChild); // quita spinner + fallback
        box.appendChild(frag);
      } else {
        revealFallback(box); // sin match/archivos → "Ver Factura" (modal)
      }
    });
  }

  // Un solo GET al App Proxy /apps/crm/customer-fiscal resuelve DOS cosas de la
  // misma respuesta: (1) el ejecutivo de cuenta → apunta los botones de WhatsApp
  // y llamada a él (si no hay, quedan en el número general del HTML); (2) la
  // factura de ESTA orden → descargas inline PDF/XML (ver resolveInvoiceDownloads).
  // Cache de sesión de la respuesta fiscal (facturas + ejecutivo). El GET a
  // customer-fiscal tarda ~5s (consulta el CRM), así que cachearlo hace que
  // navegar lista↔detalle sea instantáneo. TTL corto (3 min) para acotar el
  // desfase si el cliente emite una factura nueva en la sesión.
  var FISCAL_TTL = 180000;
  function fiscalCacheKey() {
    var rail = document.querySelector('[data-pp-customer]');
    return 'pp_fiscal_full_v2:' + ((rail && rail.getAttribute('data-pp-customer')) || 'anon');
  }
  function readFiscalCache() {
    try {
      var raw = sessionStorage.getItem(fiscalCacheKey());
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || !o.ts || (Date.now() - o.ts) > FISCAL_TTL) return null;
      return o.d;
    } catch (_) { return null; }
  }
  function writeFiscalCache(d) {
    try { sessionStorage.setItem(fiscalCacheKey(), JSON.stringify({ ts: Date.now(), d: d })); } catch (_) {}
  }

  // Un solo GET a /apps/crm/customer-fiscal (o su cache) resuelve DOS cosas de la
  // misma respuesta: (1) el ejecutivo de cuenta → botones de WhatsApp/llamada;
  // (2) las facturas → descargas inline PDF/XML (resolveInvoiceDownloads). En
  // error o respuesta vacía revela los fallbacks "Ver Factura" (el modal descarga
  // igual), así el spinner nunca se queda pegado.
  function resolveFiscal() {
    var waBtn = document.querySelector('[data-pp-order-wa]');
    var telBtn = document.querySelector('[data-pp-order-tel]');
    var dlBox = document.querySelector('[data-pp-invoice-dl]');
    if (!waBtn && !telBtn && !dlBox) return;

    function apply(d) {
      if (!d) { revealAllFallbacks(); return; }
      if (d.executive_whatsapp) {
        var num = String(d.executive_whatsapp).replace(/\D/g, '');
        if (num) {
          if (waBtn) {
            var text = waBtn.getAttribute('data-wa-text') || '';
            waBtn.href = 'https://wa.me/' + num + (text ? ('?text=' + text) : '');
          }
          if (telBtn) telBtn.href = 'tel:+' + num;
        }
      }
      resolveInvoiceDownloads(d);
    }

    var cached = readFiscalCache();
    if (cached) { apply(cached); return; }

    fetch('/apps/crm/customer-fiscal', { method: 'GET', credentials: 'same-origin' })
      .then(function (r) { return r.json().catch(function () { return null; }); })
      .then(function (d) {
        if (d) writeFiscalCache(d);
        apply(d);
      })
      .catch(function () { revealAllFallbacks(); });
  }

  function init() {
    document.querySelectorAll('[data-pp-repurchase]').forEach(bindRepurchase);
    resolveFiscal();
  }

  // La lista de órdenes de empresa (pp-company-orders.js) pinta sus cards por
  // fetch, después de que este init ya corrió. Al terminar dispara este evento
  // para que sus slots [data-pp-invoice-dl] también se resuelvan a "Descargar
  // PDF"/"Descargar XML" en vez de quedarse con el spinner. resolveFiscal lee
  // primero la cache de sesión, así que re-correrlo no pega otra vez al CRM.
  //
  // Solo resolveFiscal, a propósito: bindRepurchase NO se protege contra doble
  // binding, y la lista de empresa ya trae su propio handler de recompra. Re-
  // bindear aquí agregaría los productos dos veces al carrito.
  document.addEventListener('pp:orders-rendered', resolveFiscal);

  // Cuenta demo (pp-cuenta-demo-orden.js): el detalle ficticio nace después
  // de init. Sólo se bindea lo de ESE contenedor, que nadie más bindeó.
  document.addEventListener('pp:orden-demo-pintada', function () {
    document.querySelectorAll('[data-pp-demo-orden] [data-pp-repurchase]').forEach(bindRepurchase);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
